import { Combobox, type ComboboxItem } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useConversationPersonNames, useConversationsEmployees } from '@/entities/conversations';
import { useComboboxServerSearch } from '@/shared/hooks';
import { getShortName } from '@/shared/lib';
import { ListTruncationNotice } from '@/shared/ui';
import type { RecordPropertyValueName, RecordValueSetForm } from '../model/recordValueSet';

/** Сколько сотрудников предлагать: нужного находят поиском по ФИО, а не листают */
const EMPLOYEE_SUGGESTIONS_LIMIT = 50;

type Props = {
  /** Имя поля значения в форме набора */
  name: RecordPropertyValueName;
  disabled: boolean;
};

/**
 * Выбор сотрудника в строке набора значений: поле с выпадающим списком и поиском по ФИО на сервере.
 * Значение поля — идентификатор сотрудника; пока сотрудник не выбран, значение пусто.
 */
export const RecordEmployeeField: FC<Props> = ({ name, disabled }) => {
  const { t } = useTranslation(['records']);
  const { control } = useFormContext<RecordValueSetForm>();
  const value = useWatch({ control, name });
  const { search, searchProps } = useComboboxServerSearch();

  const selectedId = typeof value === 'number' ? value : undefined;
  const { data, isFetching } = useConversationsEmployees({
    limit: EMPLOYEE_SUGGESTIONS_LIMIT,
    'employee-name': search || undefined,
  });
  // Выбранного сотрудника может не быть среди найденных — его ФИО берётся по идентификатору
  const { names } = useConversationPersonNames({
    employeeIds: selectedId === undefined ? [] : [selectedId],
    clientIds: [],
  });

  const options = useMemo<ComboboxItem[]>(() => {
    const foundOptions = (data?.employees ?? []).map((employee) => ({
      value: employee.id,
      label: getShortName(employee.firstName, employee.lastName, employee.middleName),
    }));
    if (selectedId === undefined) return foundOptions;
    const selectedName = names[selectedId];
    if (!selectedName || foundOptions.some((option) => option.value === selectedId)) return foundOptions;
    return [{ value: selectedId, label: selectedName }, ...foundOptions];
  }, [data?.employees, names, selectedId]);

  return (
    <div className="flex w-full flex-col gap-1">
      <Combobox
        control={control}
        name={name}
        options={options}
        {...searchProps}
        isLoading={isFetching}
        placeholder={t('records:valueSet.employeePlaceholder')}
        searchPlaceholder={t('records:valueSet.employeeSearch')}
        emptyMessage={t('records:valueSet.employeesNotFound')}
        disabled={disabled}
        className="w-full"
      />
      <ListTruncationNotice
        shown={data?.employees.length ?? 0}
        total={data?.total}
        isSpaceReserved
        className="text-xs"
      />
    </div>
  );
};
